import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { IconButton, Skeleton } from '@mui/material';
import { ArrowBack } from '@mui/icons-material';

import Page from '@/components/Page';
import Stack from '@/components/Stack';
import { Title, Small } from '@/components/Typography';
import Badge from '@/components/Badge';
import { WorkersV2API, ConsolidatedSkill } from '@/services/workersV2API';
import { SkillPanel } from './components';

const SkillDetailPage = () => {
  const { workerId, skillId } = useParams<{
    workerId: string;
    skillId: string;
  }>();
  const navigate = useNavigate();

  const [skill, setSkill] = useState<ConsolidatedSkill | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSkill = async () => {
    if (!workerId || !skillId) return;

    try {
      setLoading(true);
      setError(null);

      const response = await WorkersV2API.getConsolidatedSkill(
        parseInt(workerId),
        parseInt(skillId),
      );

      if (response.success) {
        setSkill(response.data);
      } else {
        setError('Failed to fetch skill details');
      }
    } catch (err) {
      console.error('Error fetching skill details:', err);
      setError('Failed to fetch skill details');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSkill();
  }, [workerId, skillId]);

  const handleBack = () => {
    navigate(`/workers_v2/${workerId}`);
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'approved':
        return <Badge label="Approved" status="theme" />;
      case 'rejected':
        return <Badge label="Rejected" status="danger" />;
      case 'in_progress':
        return <Badge label="In Progress" status="warning" />;
      default:
        return <Badge label="Unknown" status="neutral" />;
    }
  };

  if (loading) {
    return (
      <Page
        title={<Skeleton width={200} height={40} />}
        media={
          <IconButton onClick={handleBack}>
            <ArrowBack />
          </IconButton>
        }
      >
        <Stack vertical gap={24}>
          <Skeleton variant="rounded" height={400} />
        </Stack>
      </Page>
    );
  }

  if (error || !skill) {
    return (
      <Page
        title="Skill Not Found"
        media={
          <IconButton onClick={handleBack}>
            <ArrowBack />
          </IconButton>
        }
      >
        <Stack vertical gap={24}>
          <Small color="lighter">{error || 'Skill not found'}</Small>
        </Stack>
      </Page>
    );
  }

  return (
    <Page
      title={
        <Stack vertical gap={8}>
          <Title>{skill.skill_name}</Title>
          {skill.skill_name_es && (
            <Small color="lighter">{skill.skill_name_es}</Small>
          )}
        </Stack>
      }
      media={
        <IconButton onClick={handleBack}>
          <ArrowBack />
        </IconButton>
      }
      badge={getStatusBadge(skill.status)}
    >
      <SkillPanel
        skill={skill}
        workerId={parseInt(workerId!)}
        onUpdate={fetchSkill}
      />
    </Page>
  );
};

export default SkillDetailPage;
